import {
  Box,
  Button,
  Chip,
  IconButton,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { SavedRemark, SavedRemarkFile } from '../../../types/savedRemarks';
import { formatFileSize } from '../../../utils/format';
import { FileTypeIcon } from './RemarkForm';

const formatDate = (date: Date) => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface SavedRemarkListItemProps {
  remark: SavedRemark;
  /** Edit and delete are locked while a form is open or a save is running. */
  actionsDisabled: boolean;
  /** Files can be removed from here unless this remark is being edited. */
  canDeleteFiles: boolean;
  onInsert: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onOpenFile: (file: SavedRemarkFile) => void;
  onDeleteFile: (fileId: number) => void;
}

/** One saved remark in the dialog's list: its text, price and files, and its actions. */
export default function SavedRemarkListItem({
  remark,
  actionsDisabled,
  canDeleteFiles,
  onInsert,
  onEdit,
  onDelete,
  onOpenFile,
  onDeleteFile,
}: SavedRemarkListItemProps) {
  return (
    <ListItem
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 2,
        py: 1.25,
        gap: 2,
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        flexDirection: { xs: 'column', sm: 'row' },
        '&:hover': { bgcolor: 'grey.50', borderColor: 'grey.300' }
      }}
    >
      <ListItemText
        sx={{ my: 0, minWidth: 0 }}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" component="span" sx={{ fontWeight: 600 }}>
              {remark.title}
            </Typography>
            {remark.usageCount > 0 && (
              <Chip
                label={`Used ${remark.usageCount}x`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        }
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          <Box component="span" sx={{ display: 'block' }}>
            <Typography variant="body2" color="text.primary" sx={{ mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {remark.content}
            </Typography>
            {remark.price && (
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                Price: ${remark.price.toFixed(2)}
              </Typography>
            )}
            {remark.files && remark.files.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <AttachFileIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {remark.files.length} file{remark.files.length !== 1 ? 's' : ''} attached
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {remark.files.map((file) => (
                    <Tooltip key={file.id} title="Click to download • Right-click for menu">
                      <Chip
                        icon={<FileTypeIcon mimeType={file.mimeType} />}
                        label={`${file.originalFilename} (${formatFileSize(file.fileSize)})`}
                        size="small"
                        variant="outlined"
                        color="info"
                        onDelete={canDeleteFiles ? () => onDeleteFile(file.id) : undefined}
                        onClick={() => onOpenFile(file)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'info.light',
                            color: 'info.contrastText'
                          }
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              Updated: {formatDate(remark.updatedAt)}
            </Typography>
          </Box>
        }
      />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            onClick={onInsert}
            sx={{ mr: 0.5, minWidth: 72 }}
          >
            Insert
          </Button>
          <IconButton
            size="small"
            aria-label={`Edit remark ${remark.title}`}
            onClick={onEdit}
            disabled={actionsDisabled}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Delete remark ${remark.title}`}
            onClick={onDelete}
            color="error"
            disabled={actionsDisabled}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
    </ListItem>
  );
}
